// Voice agents live in the Toolbox world (DataFabric/AppMarket DB), keyed by the
// Toolbox companyId — which differs from the IA.AI portal companyId. So the agent
// list is fetched from DataFabric DIRECTLY using the Toolbox token (same pattern
// as quoteProposalService), NOT via the AMPApi proxy / IA.AI identity.
export interface MomentumVoiceStatus {
  isEnabled: boolean;
}

// Toolbox-scoped auth for DataFabric calls that must run as the Toolbox company.
export function getToolboxHeaders(): HeadersInit {
  const token = localStorage.getItem('toolboxToken') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let msg = `API error: ${response.status}`;
    try {
      const body = await response.json();
      msg = body?.error?.message || body?.message || body?.ErrorMessage || msg;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return response.json() as Promise<T>;
}

export async function getMomentumVoiceStatus(): Promise<MomentumVoiceStatus> {
  // Direct to DataFabric with the Toolbox token, so the master switch is stored
  // under the same (Toolbox) company the webhook resolves from agent_id.
  const response = await fetch(
    `${process.env.REACT_APP_DataFabric_Url}/api/IGIGMomentumVoice/get/integration-state`,
    {
      method: 'GET',
      headers: getToolboxHeaders(),
    },
  );
  // ASP.NET serializes camelCase (isEnabled); read both casings to be safe.
  const data = await handleResponse<{
    IsEnabled?: boolean;
    isEnabled?: boolean;
  }>(response);
  return { isEnabled: data.IsEnabled ?? data.isEnabled ?? false };
}

/**
 * Reads the MyAMP company id from the authToken (MyAMP identity) JWT payload.
 * The integration row is keyed by the Toolbox company, but MyAMP resolves its
 * user by the MyAMP company id — so we send it as IntegrationId and DataFabric
 * stores it, then forwards it to MyAMP /momentum-voice/process. Returns null if
 * the token is absent/unparseable or has no company claim.
 */
function decodeAuthTokenPayload(): Record<string, unknown> | null {
  const token = localStorage.getItem('authToken') || '';
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    // base64url → base64, then decode the JWT payload.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function getMyAmpCompanyId(): number | null {
  const payload = decodeAuthTokenPayload();
  if (!payload) return null;
  // The authToken carries the claim as PascalCase "CompanyId" (a string, e.g. "45").
  const id = Number(payload.CompanyId ?? null);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/**
 * Reads the MyAMP USER id from the authToken (MyAMP identity) JWT payload.
 * Sent as IntegrationUserId so DataFabric stores it and forwards it to MyAMP
 * /momentum-voice/process — which assigns the resulting kanban task to THIS
 * exact user instead of guessing one from the company (which mis-assigned when
 * a company had multiple users). Returns null if absent/unparseable.
 */
export function getMyAmpUserId(): number | null {
  const payload = decodeAuthTokenPayload();
  if (!payload) return null;
  // The authToken carries the claim as PascalCase "UserId" (a string, e.g. "51").
  const id = Number(payload.UserId ?? null);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export async function setMomentumVoiceState(enabled: boolean): Promise<void> {
  const integrationId = getMyAmpCompanyId();
  const integrationUserId = getMyAmpUserId();
  const response = await fetch(
    `${process.env.REACT_APP_DataFabric_Url}/api/IGIGMomentumVoice/save/integration-state`,
    {
      method: 'POST',
      headers: getToolboxHeaders(),
      body: JSON.stringify({
        IsEnabled: enabled,
        IntegrationId: integrationId,
        IntegrationUserId: integrationUserId,
      }),
    },
  );
  await handleResponse<unknown>(response);
}

export async function enableMomentumVoice(): Promise<void> {
  await setMomentumVoiceState(true);
}

export async function disableMomentumVoice(): Promise<void> {
  await setMomentumVoiceState(false);
}

// ── Per-agent integration state ─────────────────────────────────────────────

export interface MomentumVoiceAgentState {
  ElevenLabsAgentId: string;
  IsEnabled: boolean;
}

/**
 * Returns the per-agent integration states for the company. Agents not present
 * in the result are treated as disabled (opt-in).
 */
export async function getMomentumVoiceAgentStates(): Promise<
  MomentumVoiceAgentState[]
> {
  // Direct to DataFabric with the Toolbox token, so state is keyed to the same
  // (Toolbox) company that owns the agents.
  const response = await fetch(
    `${process.env.REACT_APP_DataFabric_Url}/api/IGIGMomentumVoice/get/agent-integration-state`,
    {
      method: 'GET',
      headers: getToolboxHeaders(),
    },
  );
  // API returns camelCase; normalize to PascalCase (read both to be safe).
  const data = await handleResponse<
    Array<{
      ElevenLabsAgentId?: string;
      elevenLabsAgentId?: string;
      IsEnabled?: boolean;
      isEnabled?: boolean;
    }>
  >(response);
  if (!Array.isArray(data)) return [];
  return data.map((s) => ({
    ElevenLabsAgentId: s.ElevenLabsAgentId ?? s.elevenLabsAgentId ?? '',
    IsEnabled: s.IsEnabled ?? s.isEnabled ?? false,
  }));
}

/** Enable/disable the integration for a single voice agent. */
export async function setMomentumVoiceAgentState(
  elevenLabsAgentId: string,
  enabled: boolean,
): Promise<void> {
  // Direct to DataFabric with the Toolbox token (same company as the agents).
  const response = await fetch(
    `${process.env.REACT_APP_DataFabric_Url}/api/IGIGMomentumVoice/save/agent-integration-state`,
    {
      method: 'POST',
      headers: getToolboxHeaders(),
      body: JSON.stringify({
        ElevenLabsAgentId: elevenLabsAgentId,
        IsEnabled: enabled,
        // MyAMP user id — stored so the per-agent forward assigns the task to
        // this exact user.
        IntegrationUserId: getMyAmpUserId(),
      }),
    },
  );
  await handleResponse<unknown>(response);
}

// ── Company's voice agents (DataFabric direct, Toolbox token — Toolbox company) ───

export interface MomentumVoiceAgent {
  ElevenLabsAgentId: string;
  AgentName: string | null;
}

/**
 * Returns the company's voice agents from DataFabric (GetMomentumVoiceAgentByUserId).
 * Used to populate the Settings modal agent list.
 */
export async function getMomentumVoiceAgents(): Promise<MomentumVoiceAgent[]> {
  const response = await fetch(
    `${process.env.REACT_APP_DataFabric_Url}/api/IGIGMomentumVoice/get/agents`,
    {
      method: 'GET',
      headers: getToolboxHeaders(),
    },
  );
  // DataFabric serializes camelCase; normalize to PascalCase (read both).
  const data = await handleResponse<
    Array<{
      ElevenLabsAgentId?: string;
      elevenLabsAgentId?: string;
      AgentName?: string | null;
      agentName?: string | null;
    }>
  >(response);
  if (!Array.isArray(data)) return [];
  return data.map((a) => ({
    ElevenLabsAgentId: a.ElevenLabsAgentId ?? a.elevenLabsAgentId ?? '',
    AgentName: a.AgentName ?? a.agentName ?? null,
  }));
}
